/* ==============================================================================
   Agendamento longo, sem transbordar o temporizador
   ==============================================================================
   Existe por um defeito de produção que custou 1,17 MILHÃO de linhas no banco.

   `setTimeout` guarda o atraso num inteiro de 32 bits COM SINAL. O teto é
   2.147.483.647 ms — 24,86 dias. Passar disso não é arredondado nem recusado: o
   valor transborda, vira negativo, e o temporizador dispara IMEDIATAMENTE.

   O token de acesso do Concord dura 30 dias. Trinta dias são 2.592.000.000 ms,
   que transbordam por 444 milhões; `2592000000 | 0` é −1702967296.

   O agendador de renovação fazia:

       agenda para daqui a 30 dias  →  dispara agora
       renova o token               →  ganha outros 30 dias
       agenda para daqui a 30 dias  →  dispara agora

   Um laço limitado só pelo tempo de ida e volta da rede. Cada volta rotaciona o
   token de atualização, e rotação grava uma sessão nova. Medido: 677.454
   sessões num único dia, com 13 usuários cadastrados.

   O defeito nasceu no dia em que o TTL foi de 10 minutos para 30 dias. Com 10
   minutos o atraso cabia folgado, e o limite nunca era encostado — a mudança de
   configuração estava certa e revelou um limite que o código nunca tinha
   tocado.

   Isto vive em `shared` e não no cliente web porque a aritmética é a mesma em
   qualquer cliente que agende algo distante — e é exatamente o tipo de conta
   que ninguém redescobre por vontade própria: ela não falha, ela dispara cedo
   demais e parece que "a renovação está funcionando bem".
============================================================================== */

/**
 * Maior atraso que `setTimeout` aceita sem transbordar: 2^31 − 1 ms.
 *
 * Não é um limite escolhido — é onde o inteiro de 32 bits com sinal vira
 * negativo.
 */
export const MAX_TIMEOUT_MS = 2_147_483_647;

export interface RenewalPlan {
  /**
   * `wait` = só dormir e reavaliar; `run` = a hora chegou, execute a ação.
   *
   * A distinção é o conserto inteiro. Sem ela, "o alvo está longe demais para
   * um temporizador" e "chegou a hora" produzem o mesmo disparo — e o segundo
   * significado é o que o código assumia.
   */
  kind: 'wait' | 'run';
  delayMs: number;
}

/**
 * Quanto dormir antes da próxima avaliação da renovação.
 *
 * @param targetAt  instante-alvo em epoch ms (quando a ação deve rodar)
 * @param now       agora, em epoch ms
 * @param minDelay  piso, para um alvo já vencido não virar laço quente
 */
export function planRenewal(
  targetAt: number,
  now: number,
  minDelay = 5_000,
): RenewalPlan {
  const remaining = targetAt - now;

  /*
   * Alvo além do teto: dorme o máximo possível e reavalia.
   *
   * `kind: 'wait'` e não `'run'` — ao acordar ainda faltam dias, e renovar ali
   * seria renovar cedo. Renovar cedo é justamente o que produzia o laço: cada
   * renovação empurra o alvo para 30 dias adiante, que transborda de novo.
   */
  if (remaining > MAX_TIMEOUT_MS) {
    return { kind: 'wait', delayMs: MAX_TIMEOUT_MS };
  }

  /*
   * O piso vale inclusive para alvo VENCIDO (remaining negativo).
   *
   * Um alvo no passado com atraso 0 produziria uma tentativa por quadro
   * enquanto a renovação falhasse — o mesmo laço quente por outra porta.
   */
  return { kind: 'run', delayMs: Math.max(minDelay, remaining) };
}
